import { apiConfig } from '../config/api';
import { Deck, DeckSummary } from '../types';

class DeckService {
  async getDeck(deckId: string, token: string): Promise<Deck> {
    const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.deck(deckId)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch deck');
    }

    const data = await response.json();
    return data.data.deck; // Backend returns {data: {deck: {...}}}
  }

  async getDecks(
    options: {
      limit?: number;
      offset?: number;
      sortBy?: 'uploadedAt' | 'fileName';
      sortOrder?: 'asc' | 'desc';
      status?: string;
      search?: string;
    },
    token: string
  ): Promise<{ decks: DeckSummary[]; total: number }> {
    const params = new URLSearchParams();
    
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);

    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.decks}?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch decks');
    }

    const data = await response.json();
    return data.data;
  }

  async deleteDeck(deckId: string, token: string): Promise<void> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.deleteDeck(deckId)}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete deck');
    }
  }

  async getDeckStatus(deckId: string, token: string): Promise<any> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.deckStatus(deckId)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch deck status');
    }

    const data = await response.json();
    return data.data;
  }

  async uploadPPT(
    file: File,
    selectedTasks: string[],
    token: string,
    onProgress?: (progress: number) => void
  ): Promise<Deck> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('selectedTasks', JSON.stringify(selectedTasks));

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = (e.loaded / e.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response.data);
          } catch (error) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('POST', `${apiConfig.baseUrl}${apiConfig.endpoints.upload}`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  }
}

export const deckService = new DeckService();
export default DeckService;

